import api from './api';

export interface Feature {
  id: string;
  key: string;
  name: string;
  description?: string;
  planType: 'free' | 'paid' | 'premium' | 'enterprise';
  displayOrder: number;
}

export interface FeatureStatus {
  key: string;
  name: string;
  description?: string;
  planType: string;
  enabled: boolean;
  displayOrder: number;
}

export interface FeaturesStatusResponse {
  enabledFeatures: string[];
  features: FeatureStatus[];
}

/**
 * Get all features with their enabled status for the current tenant
 * This is the main endpoint for UI to determine which menu items to show
 */
export async function getFeaturesStatus(): Promise<FeaturesStatusResponse> {
  // API is hosted on its own subdomain (api.arrangelly.local), no /api prefix needed
  const response = await api.get<FeaturesStatusResponse>('/features/status');
  const data = response.data || { enabledFeatures: [], features: [] };

  // Normalize to avoid undefined/null issues in consumers
  return {
    enabledFeatures: Array.isArray(data.enabledFeatures) ? data.enabledFeatures : [],
    features: Array.isArray(data.features) ? data.features : [],
  };
}

/**
 * Get all available features from the catalog
 */
export async function getFeaturesCatalog(): Promise<Feature[]> {
  const response = await api.get<Feature[]>('/features/catalog');
  return response.data;
}

/**
 * Get features by plan type
 */
export async function getFeaturesByPlanType(planType: string): Promise<Feature[]> {
  const response = await api.get<Feature[]>(`/features/catalog/${planType}`);
  return response.data;
}

/**
 * Check if a specific feature is enabled
 */
export async function checkFeature(featureKey: string): Promise<{ featureKey: string; enabled: boolean }> {
  const response = await api.get<{ featureKey: string; enabled: boolean }>(`/features/check/${featureKey}`);
  return response.data;
}

/**
 * Check if a feature is enabled (quick helper)
 */
export function isFeatureEnabled(featureKey: string, enabledFeatures: string[] | undefined | null): boolean {
  if (!enabledFeatures || !Array.isArray(enabledFeatures)) {
    return false;
  }
  return enabledFeatures.includes(featureKey);
}

